import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { IoChevronForward } from "react-icons/io5";
import Main from "../../components/layout/Main";
import AppModal from "../../components/layout/app/AppModal";
import AlertDialog from "../../components/layout/app/AlertDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import { apiMessage } from "../../api/apiMessage";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import DevicePicker from "./DevicePicker";
import { Person, Section, ShareOptions, VoiceToggles } from "./parts";
import {
  EMAIL,
  SHARE_PATHS,
  asList,
  initialOf,
  personInitials,
  personName,
  roleLabel,
  shareSummary,
  sharedMessage,
  voiceOn,
  voiceSummary,
} from "./shareUtils";
import "../../assets/styles/app-share.css";

const NOT_FOUND = "This email address isn't registered with HASHh. Please check it and try again.";
const SEARCH_FAILED = "Couldn't look up this address. Please try again.";

// The app's Share Property screen (app/screens/ShareProperty/UserSearch.js).
// The property's creator invites people by email and sees who they've shared
// it with. Everyone else, owners it was shared with included, sees who shared
// it with them and can leave it.
function ShareProperty() {
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const { profileName, selectProperty, selectedPropertey, setPropertyOwner } = useUser();
  const propertyId = User.getPropertyId();

  // config, this user's own share (mine) and the people they shared it with.
  const [data, setData] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState("");
  const [search, setSearch] = useState({ status: "idle", results: [] });
  const [invitee, setInvitee] = useState(null);
  const [role, setRole] = useState("");
  const [shareAll, setShareAll] = useState(false);
  const [deviceIds, setDeviceIds] = useState([]);
  const [shareWithGoogle, setShareWithGoogle] = useState(true);
  const [shareWithAlexa, setShareWithAlexa] = useState(true);
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState(false);

  const [dialog, setDialog] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [houses, setHouses] = useState(null);

  useEffect(() => {
    if (!propertyId) return undefined;
    let current = true;
    setLoadFailed(false);
    Promise.all([
      UserService.getConfig(propertyId),
      UserService.getSharedUsersbyGuest(propertyId),
      UserService.getSharedUsers(propertyId),
    ])
      .then(([config, mine, shared]) => {
        if (!current) return;
        setData({ config, mine: asList(mine)[0] ?? null, shared: asList(shared) });
        setPropertyOwner({ propertyId, ownedBy: config?.ownedBy ?? null });
      })
      .catch(() => {
        if (current) setLoadFailed(true);
      });
    return () => {
      current = false;
    };
  }, [propertyId, attempt, setPropertyOwner]);

  // Looks the typed address up half a second after typing stops, once it's a
  // whole address. Only the account with exactly that address is offered, not
  // everyone the server's partial match finds.
  const email = query.trim();
  useEffect(() => {
    setSearch({ status: "idle", results: [] });
    if (!EMAIL.test(email) || invitee?.email === email) return undefined;
    let current = true;
    const timer = setTimeout(async () => {
      setSearch({ status: "searching", results: [] });
      try {
        const wanted = email.toLowerCase();
        const results = asList(await UserService.userSearch(email)).filter((person) =>
          [person.email, person.username].some((value) => value?.toLowerCase() === wanted)
        );
        if (current) setSearch({ status: results.length ? "found" : "missing", results });
      } catch (error) {
        // The API says why it refused, e.g. too many searches in a short time.
        if (current) setSearch({ status: "failed", results: [], message: apiMessage(error, SEARCH_FAILED) });
      }
    }, 500);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // Runs for a new address only; picking a person sets invitee and query together.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const house = profileName.find((item) => item.id === propertyId);
  const propertyName = house?.name ?? data?.config?.name ?? "";
  const myRole = data?.mine?.role ?? house?.role;
  const myName = data?.mine?.grantedTo ?? User.getUser();
  // Only the property's creator (its config's ownedBy) can invite people and
  // change or remove them; the API refuses everyone else, owners it was shared
  // with included.
  const isCreator = data?.config?.ownedBy ? data.config.ownedBy === myName : data?.mine?.is_superOwner === true;
  // The app shows Leave Property to anyone who isn't the super owner. The
  // property's creator can't leave either, even on an old share without the flag.
  const canLeave = Boolean(data?.mine && data.mine.is_superOwner !== true && data.mine.grantedTo !== data.config?.ownedBy);
  const canInvite = Boolean(invitee && role && (shareAll || deviceIds.length));

  const refreshShared = async () => {
    setRefreshing(true);
    try {
      const shared = asList(await UserService.getSharedUsers(propertyId));
      if (alive.current) setData((current) => current && { ...current, shared });
    } catch (error) {
      toast("Couldn't refresh the shared users. Please try again.", "red");
    } finally {
      if (alive.current) setRefreshing(false);
    }
  };

  const onQuery = (value) => {
    setQuery(value);
    if (invitee && value.trim() !== invitee.email) setInvitee(null);
  };

  // The app's checks when a person is picked (UserSearch.js onPressItem).
  const choose = (person) => {
    const label = person.email || person.username;
    setSearch({ status: "idle", results: [] });
    let problem = null;
    if (person.username === data.config?.ownedBy) problem = `"${label}" already owns this property, so you can't invite them.`;
    else if (person.username === myName) problem = "You can't invite yourself.";
    else if (data.shared.some((share) => share.grantedTo === person.username)) problem = `You have already invited "${label}".`;
    if (problem) {
      setDialog({ type: "error", title: "Validation Error", message: problem, afterClose: () => setQuery("") });
      return;
    }
    setInvitee({ username: person.username, email: label, name: personName(person), initials: personInitials(person) });
    setQuery(label);
  };

  const invite = async (event) => {
    event.preventDefault();
    if (!canInvite || sending) return;
    setSending(true);
    try {
      const response = await UserService.inviteUser(
        { grantTo: invitee.username, role, devices: shareAll ? [] : deviceIds, shareAll, shareWithGoogle, shareWithAlexa },
        propertyId
      );
      if (!alive.current) return;
      // The API keeps the existing share instead of saving a second one.
      if (response?.message === "Property already shared.") {
        setDialog({
          type: "error",
          title: "Already shared",
          message: `"${invitee.email}" already has access to this property. Open them under Shared Users to change it.`,
        });
      } else {
        setDialog({ type: "success", title: "Shared successfully!", message: sharedMessage(shareAll, deviceIds.length, invitee.email) });
        // As in the app, the role stays for the next invitation.
        setQuery("");
        setInvitee(null);
        setShareAll(false);
        setDeviceIds([]);
        setShareWithGoogle(true);
        setShareWithAlexa(true);
      }
      refreshShared();
    } catch (error) {
      if (alive.current) {
        setDialog({ type: "error", title: "Could not share", message: apiMessage(error, "Sharing failed. Please try again.") });
      }
    } finally {
      if (alive.current) setSending(false);
    }
  };

  const leave = async () => {
    setDialog(null);
    setLeaving(true);
    try {
      await UserService.deleteSharing(propertyId, data.mine.id);
    } catch (error) {
      if (alive.current) {
        setLeaving(false);
        setDialog({ type: "error", title: "Could not leave", message: apiMessage(error, "Leaving the property failed. Please try again.") });
      }
      return;
    }
    toast("You have left the property.", "green");
    let others = [];
    try {
      others = asList(await UserService.getProperty())
        .filter((item) => item.property?.id !== propertyId)
        .map((item) => ({ id: item.property.id, name: item.property.name }));
    } catch (error) {
      // Without the list, the home page picks a property as it loads.
    }
    if (!alive.current) return;
    setLeaving(false);
    if (others.length) {
      setHouses(others);
    } else {
      User.removePropertyId();
      history.replace("/dashboard");
    }
  };

  const switchTo = (next) => {
    selectProperty(next.id);
    selectedPropertey();
    history.replace("/dashboard");
  };

  const closeDialog = () => {
    const after = dialog?.afterClose;
    setDialog(null);
    if (after) after();
  };

  const searchNote =
    search.status === "searching"
      ? "Searching…"
      : search.status === "missing"
      ? NOT_FOUND
      : search.status === "failed"
      ? search.message
      : "";

  const inviteForm = (
    <form className="hx-share-form" onSubmit={invite} noValidate>
      <div className="hx-field">
        <label className="hx-field-label" htmlFor="hx-share-email">
          User
        </label>
        <input
          id="hx-share-email"
          className="hx-field-input"
          type="email"
          inputMode="email"
          autoComplete="off"
          spellCheck={false}
          placeholder="Their email address"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          aria-invalid={search.status === "missing" || undefined}
          aria-describedby="hx-share-email-note"
        />
        <p
          id="hx-share-email-note"
          className={`hx-share-note ${search.status === "missing" || search.status === "failed" ? "is-error" : ""}`}
          role="status"
        >
          {searchNote}
        </p>
        {search.results.length ? (
          <ul className="hx-share-results" aria-label="Matching accounts">
            {search.results.map((person) => (
              <li key={person.id ?? person.username}>
                <button type="button" className="hx-share-person" onClick={() => choose(person)}>
                  <Person initials={personInitials(person)} name={personName(person) || person.username} detail={person.username} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {invitee ? (
          <div className="hx-share-chosen">
            <Person initials={invitee.initials} name={invitee.name || invitee.username} detail={invitee.username} />
          </div>
        ) : null}
      </div>

      <ShareOptions
        idPrefix="hx-share-invite"
        role={role}
        onRole={setRole}
        shareAll={shareAll}
        onShareAll={setShareAll}
        deviceCount={deviceIds.length}
        onPickDevices={() => setPicking(true)}
      />

      <VoiceToggles
        idPrefix="hx-share-invite"
        google={shareWithGoogle}
        alexa={shareWithAlexa}
        onGoogle={setShareWithGoogle}
        onAlexa={setShareWithAlexa}
      />

      <button type="submit" className="hx-share-btn" disabled={!canInvite || sending}>
        {sending ? "INVITING…" : "INVITE"}
      </button>
    </form>
  );

  const propertyInfo = (
    <>
      <dl className="hx-share-info">
        <div>
          <dt>Property Name</dt>
          <dd>{propertyName}</dd>
        </div>
        <div>
          <dt>Role</dt>
          <dd>{roleLabel(myRole)}</dd>
        </div>
        <div>
          <dt>Owner Name</dt>
          <dd>{data?.mine?.grantedBy ?? "—"}</dd>
        </div>
      </dl>
      {data?.mine ? (
        <VoiceToggles
          idPrefix="hx-share-mine"
          google={voiceOn(data.mine.shareWithGoogle)}
          alexa={voiceOn(data.mine.shareWithAlexa)}
          readOnly
        />
      ) : null}
    </>
  );

  let content;
  if (!propertyId) {
    content = <div className="hx-empty">Choose a property on the home page first.</div>;
  } else if (loadFailed) {
    content = (
      <div className="hx-empty" role="alert">
        Couldn't load this property's sharing.
        <button type="button" className="hx-btn" onClick={() => setAttempt(attempt + 1)}>
          Try again
        </button>
      </div>
    );
  } else if (!data) {
    content = (
      <div className="hx-share-loading" aria-label="Loading">
        <div className="hx-skel is-tall" />
        <div className="hx-skel" />
      </div>
    );
  } else {
    content = (
      <>
        <Section id="hx-share-main" title={isCreator ? "Invite User" : "Property Info"}>
          <div className="hx-share-card">
            <h3 className="hx-share-card-title">{isCreator ? "Invite User" : "Property Info"}</h3>
            {isCreator ? inviteForm : propertyInfo}
          </div>
        </Section>

        {isCreator ? (
          <Section id="hx-share-people" title="Shared Users">
            <button type="button" className="hx-share-btn is-light" onClick={refreshShared} disabled={refreshing}>
              {refreshing ? "REFRESHING…" : "REFRESH"}
            </button>
            {data.shared.length ? (
              <ul className="hx-share-people">
                {data.shared.map((share) => (
                  <li key={share.id}>
                    <button
                      type="button"
                      className="hx-share-person"
                      onClick={() => history.push(SHARE_PATHS.USER, { share })}
                    >
                      <Person
                        initials={initialOf(share.grantedTo)}
                        name={share.grantedTo}
                        detail={`${roleLabel(share.role)} · ${shareSummary(share)}`}
                        note={voiceSummary(share)}
                      />
                      <IoChevronForward className="hx-share-go" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="hx-share-none">You haven't shared this property with anyone yet.</p>
            )}
          </Section>
        ) : null}

        {canLeave ? (
          <button
            type="button"
            className="hx-share-leave"
            disabled={leaving}
            onClick={() =>
              setDialog({
                type: "confirmation",
                title: "Leave Property",
                message: `Are you sure you want to leave the property "${propertyName}"?`,
                onConfirm: leave,
              })
            }
          >
            {leaving ? "Leaving…" : "Leave Property"}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <Main header="stack" title="Share Property" hideTabBar>
      <div className="hx-share">{content}</div>

      <DevicePicker
        open={picking}
        devices={data?.config?.devices ?? null}
        selected={deviceIds}
        onCancel={() => setPicking(false)}
        onSave={(ids) => {
          setDeviceIds(ids);
          setPicking(false);
        }}
      />

      <AlertDialog
        open={Boolean(dialog)}
        type={dialog?.type}
        title={dialog?.title}
        message={dialog?.message}
        onClose={closeDialog}
        onConfirm={dialog?.onConfirm}
      />

      {/* After leaving, as in the app: pick the property to switch to. */}
      <AppModal open={Boolean(houses)} onClose={() => {}} title="Select House">
        <p className="hx-modal-text">You have left "{propertyName}". Choose the property you'd like to switch to now.</p>
        <div className="hx-houses">
          {(houses ?? []).map((item) => (
            <button key={item.id} type="button" className="hx-house" onClick={() => switchTo(item)}>
              <span className="hx-avatar is-large" aria-hidden="true">
                {initialOf(item.name)}
              </span>
              {item.name}
            </button>
          ))}
        </div>
      </AppModal>
    </Main>
  );
}

export default ShareProperty;
